import React, { useState, useEffect } from 'react';
import styles from './SpectateModal.module.css';
import { queryActiveGames, getGameAge } from '../../services/firebase/firebase';

interface SpectateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectGame: (gameId: string) => void;
}

interface ActiveGame {
  id: string;
  lightUsername: string;
  darkUsername: string;
  lightElo?: number;
  darkElo?: number;
  turnCount: number;
  timeControl?: string;
  lastActivityMs: number | null; // milliseconds since last activity
}

/**
 * Formats milliseconds into a human-readable "time ago" string
 */
function formatTimeAgo(ms: number | null): string {
  if (ms === null) return 'Unknown';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  if (seconds > 10) return `${seconds}s ago`;
  return 'Just now';
}

function SpectateModal({ isOpen, onClose, onSelectGame }: SpectateModalProps) {
  const [games, setGames] = useState<ActiveGame[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGames = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const activeGames = await queryActiveGames();
      const formattedGames: ActiveGame[] = activeGames.map((game) => {
        const age = getGameAge(game.data);
        return {
          id: game.id,
          lightUsername: game.data.lightPlayerUsername || 'Player 1',
          darkUsername: game.data.darkPlayerUsername || 'Player 2',
          lightElo: game.data.lightElo,
          darkElo: game.data.darkElo,
          turnCount: game.data.turnCount || 0,
          timeControl: game.data.timeControlPreset || 'standard',
          lastActivityMs: age?.ageMs ?? null,
        };
      });
      setGames(formattedGames);
    } catch (err) {
      setError('Failed to load games. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchGames();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatTimeControl = (tc?: string) => {
    if (!tc) return 'Standard';
    return tc.charAt(0).toUpperCase() + tc.slice(1);
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Spectate a Game</h2>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <span>Loading games...</span>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <span>{error}</span>
              <button className={styles.retryButton} onClick={fetchGames}>
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && games.length === 0 && (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>👁</span>
              <span className={styles.emptyText}>No active games to spectate</span>
              <span className={styles.emptySubtext}>
                Check back later when players are in a match
              </span>
            </div>
          )}

          {!isLoading && !error && games.length > 0 && (
            <div className={styles.gameList}>
              {games.map((game) => (
                <button
                  key={game.id}
                  className={styles.gameItem}
                  onClick={() => onSelectGame(game.id)}
                >
                  <div className={styles.players}>
                    <div className={styles.player}>
                      <span className={styles.playerColor}>Light</span>
                      <span className={styles.playerName}>{game.lightUsername}</span>
                      {game.lightElo && (
                        <span className={styles.playerElo}>({game.lightElo})</span>
                      )}
                    </div>
                    <span className={styles.vs}>vs</span>
                    <div className={styles.player}>
                      <span className={styles.playerColor}>Dark</span>
                      <span className={styles.playerName}>{game.darkUsername}</span>
                      {game.darkElo && (
                        <span className={styles.playerElo}>({game.darkElo})</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.gameInfo}>
                    <span className={styles.turnCount}>Turn {game.turnCount}</span>
                    <span className={styles.lastActivity}>
                      {formatTimeAgo(game.lastActivityMs)}
                    </span>
                    <span className={styles.timeControl}>
                      {formatTimeControl(game.timeControl)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.refreshButton} onClick={fetchGames} disabled={isLoading}>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SpectateModal;
